import ProgressivePromise from '@aytm/lib/AsyncJobManager/ProgressivePromise'
import railsPostFetch, { getCSRFToken } from '@aytm/lib/common/rails-post-fetch'
import fetch from 'isomorphic-fetch'
import uploadWithProgress from '@aytm/lib/common/upload-with-progress'
import { headersFromGoogleUploadUrl } from '@aytm/lib/common/google-cloud-url'
import exceptionReporter from '@aytm/lib/common/exception-reporter'

export default function ImageAssetUploadJob({ createAssetPath, file, params = {} } = {}) {
  /**
   * @returns {ProgressivePromise}
   */
  return () =>
    new ProgressivePromise((resolve, reject, progress) => {
      const totalProgress = file.size
      // CREATE ASSET

      progress(0, totalProgress) // set progress to 0

      const createParams = Object.assign({}, params, {
        filename: file.name,
        content_type: file.type,
        filesize: file.size
      })
      const asset = {}

      railsPostFetch(createAssetPath, {
        body: JSON.stringify(createParams)
      })
        .then(response => {
          if (response.ok) {
            return response.json()
          } else {
            exceptionReporter(new Error(`Failed to create asset: ${response.status} ${response.statusText}`), {
              createAssetPath,
              params,
              status: response.status
            })
            return {}
          }
        })

        // UPLOAD IMAGE
        .then(createdAsset => {
          Object.assign(asset, createdAsset)

          if (
            !asset.uid ||
            'uploading' !== asset.status ||
            !asset.upload_urls ||
            !asset.upload_urls.binary_upload_url ||
            !asset.upload_urls.finish_upload_url ||
            !asset.upload_urls.status_upload_url
          ) {
            // the server's refusal rides along under its own key, so callers reading the Error see no change
            return Promise.reject(
              Object.assign(new Error('createAsset - invalid response data'), { asset: { ...asset } })
            )
          }

          return uploadWithProgress(
            asset.upload_urls.binary_upload_url,
            {
              method: 'PUT',
              headers: {
                'Content-Type': file.type,
                ...headersFromGoogleUploadUrl(asset.upload_urls.binary_upload_url)
              },
              body: file
            },
            progressEvent => {
              progress(Math.round(progressEvent.loaded * 0.9), totalProgress) // set progress for uploaded bytes with correction 100% -> 90%
            }
          )
        })

        // FINISH UPLOAD
        .then(() =>
          fetch(asset.upload_urls.finish_upload_url, {
            method: 'POST',
            headers: {
              'X-CSRF-Token': getCSRFToken()
            },
            mode: 'cors'
          })
        )

        // CHECK STATUS
        .then(finishResponse => {
          if (finishResponse.ok) {
            progress(Math.round(file.size * 0.95), totalProgress) // set progress to 95%
            return new Promise((resolve, reject) => {
              const checkStatus = () => {
                fetch(asset.upload_urls.status_upload_url, {
                  method: 'POST',
                  mode: 'cors',
                  headers: {
                    'X-CSRF-Token': getCSRFToken()
                  },
                  Accept: 'application/json'
                })
                  .then(response => (response.ok ? response.json() : {}))
                  .then(assetStatus => {
                    switch (assetStatus.status) {
                      case 'ready':
                      case 'source_valid': {
                        progress(totalProgress, totalProgress) // set progress to 100%
                        resolve(
                          Object.assign({}, asset, assetStatus, {
                            status: 'ready',
                            fileName: file.name,
                            contentType: file.type,
                            fileSize: file.size
                          })
                        )
                        break
                      }
                      case 'processing': {
                        setTimeout(checkStatus, 1000)
                        break
                      }
                      default: {
                        reject(assetStatus)
                        break
                      }
                    }
                  })
                  .catch(error => {
                    exceptionReporter(error, {
                      context: 'checkStatus',
                      file: file.name,
                      assetUid: asset.uid,
                      statusUrl: asset.upload_urls?.status_upload_url
                    })
                    setTimeout(checkStatus, 1000)
                  })
              }
              setTimeout(checkStatus, 500)
            })
          } else {
            return Promise.reject(new Error('unable to finish upload'))
          }
        })
        .then(resolve, reject)
        .catch(error => {
          exceptionReporter(error, { file: file.name, createAssetPath, params })
          reject(error)
        })
    })
}
